'use client';
/* eslint-disable react/prop-types */
import React from 'react';
import { useAppSelector } from '@/lib/hooks';
import { useAppStore, useAppDispatch } from '@/lib/hooks';
import Button from '@/components/ui/Button';
import { useRouter } from 'next/navigation';
import ProgressUtilityService from '@/services/course/progress-utility-service';
import UserProgressStorageService from '@/services/storage/user-progress-storage-service';
import {
  ActivityData,
  CourseData,
  SubunitData,
  UnitData,
  ActivityProgressCompletionData,
  SubunitId,
  UnitId,
} from 'course-types';
import { ChevronDownIcon, ChevronRightIcon } from '@/assets/icons/chevron';
import ActivityIcon from '@/components/ActivityIcon';
import { CircleXIcon, HomeIcon } from 'lucide-react';
import { CheckCircleIcon } from '@/assets/ui/activity';
import { useSetActivity } from '@/common/hooks/useSetActivity';

interface ProgressNavProps {
  onClick?: () => void;
}

function ProgressNav({ onClick }: ProgressNavProps): React.JSX.Element {
  const store = useAppStore();
  const router = useRouter();
  const dispatch = useAppDispatch();

  const [openUnit, setOpenUnit] = React.useState(-1);
  const [openSubunit, setOpenSubunit] = React.useState(-1);
  // Re-render when user progress cookie updates
  const [refreshTick, setRefreshTick] = React.useState(0);
  React.useEffect(() => {
    const handler = () => setRefreshTick((t) => t + 1);
    if (typeof window !== 'undefined') {
      window.addEventListener('user-progress-updated', handler as EventListener);
    }
    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('user-progress-updated', handler as EventListener);
      }
    };
  }, []);

  const course: CourseData = useAppSelector(() => store.getState().course.activeCourseData);
  const currentActivity = useAppSelector(() => store.getState().activity);

  const setActivity = useSetActivity();

  const { total, completed } = ProgressUtilityService.getCourseCompletionProgress(course.id);
  const completionPercentage = ProgressUtilityService.calculateCourseProgress(course.id);

  const DEFAULT_PROGRESS: ActivityProgressCompletionData = {
    video: {},
    document: {},
    assignment: {},
    quiz: {},
  };

  const progressCookie = ProgressUtilityService.validateCourseDataExists(
    UserProgressStorageService.getUserCourseProgressCookie() ?? {},
    course.id,
  );

  const isActivityCompleted = (
    activity: ActivityData,
    progress: ActivityProgressCompletionData,
  ): boolean => {
    if (activity.type === 'resource') return false;

    if (activity.type === 'quiz') {
      const quizNode = (progress[activity.type] ?? {})[activity.id];
      return !!(quizNode && quizNode?.initialSubmissionDate);
    } else {
      return !!(progress[activity.type] ?? {})[activity.id]?.submissionDate;
    }
  };

  const handleActivityClick = (
    activity: ActivityData,
    unit: UnitData,
    subunit?: SubunitData & { id: string },
    unitId?: string,
  ) => {
    setActivity(
      {
        ...activity,
        description: activity.description,
        url: activity.url,
        subunit_id: subunit?.id,
        unit_id: unitId,
      },
      {
        subunit: subunit,
        unitName: unit.name,
        unitId: unitId,
        courseName: course.name,
        courseId: course.id,
        skipNavigation: true,
      },
    );
  };

  const ActivityItem = ({
    activity,
    unit,
    subunit,
    unitId,
    progress,
  }: {
    activity: ActivityData;
    unit: UnitData;
    subunit?: SubunitData & { id: string };
    unitId: string;
    progress: ActivityProgressCompletionData;
  }) => {
    const completed = isActivityCompleted(activity, progress);

    return (
      <div
        onClick={() => handleActivityClick(activity, unit, subunit, unitId)}
        className={`py-2 pr-[21px] pl-[16px] flex items-center justify-between w-full ${activity.id === currentActivity.id ? 'text-gray-100 bg-primary rounded-lg' : ''}`}
      >
        <div
          className={`flex gap-1 text-sm gap-1 items-center justify-center ${activity.id === currentActivity.id ? 'text-gray-100' : ''}`}
        >
          <div className='flex w-6 w-6 items-center justify-center'>
            <ActivityIcon
              type={activity.type}
              size='small'
              className={`p-[2px] ${activity.id === currentActivity.id ? 'text-gray-100' : ''}`}
            />
          </div>
          {activity.name}
        </div>
        {completed && (
          <div className='flex w-4 w-4 items-center justify-center'>
            <CheckCircleIcon
              className={`${activity.id === currentActivity.id ? 'text-gray-100' : 'text-green-500'} w-4 h-4`}
            />
          </div>
        )}
      </div>
    );
  };

  const DropdownMenu = ({ unit, unitId }: { unit: UnitData; unitId: string }) => {
    return (
      <div className='flex flex-col gap-2'>
        <div
          className='flex items-center hover:cursor-pointer gap-2 py-2 px-[14px] font-semibold'
          onClick={() => {
            setOpenUnit(openUnit === unit.index ? -1 : unit.index);
            setOpenSubunit(-1);
          }}
        >
          {openUnit === unit.index ? (
            <ChevronDownIcon className='w-5 h-5' />
          ) : (
            <ChevronRightIcon className='w-5 h-5' />
          )}{' '}
          {unit.name}
        </div>
        {/* subunits */}
        {openUnit === unit.index &&
          Object.entries(unit.subunits).map(([subunitId, subunit]) => (
            <div
              key={subunit.index}
              className='flex flex-col items-start hover:cursor-pointer px-[16px]'
            >
              <div
                onClick={() => setOpenSubunit(openSubunit === subunit.index ? -1 : subunit.index)}
                className='flex items-center gap-2 py-2 px-[14px] w-full font-semibold text-sm'
              >
                {openSubunit === subunit.index ? (
                  <ChevronDownIcon className='w-5 h-5' />
                ) : (
                  <ChevronRightIcon className='w-5 h-5' />
                )}{' '}
                {subunit.name}
              </div>
              <div className='w-full flex flex-col pl-[14px]'>
                {openSubunit === subunit.index &&
                  subunit.activities.map((activity) => {
                    const subunitProgress =
                      progressCookie[course.id].completion_data.subunits[subunitId as SubunitId] ??
                      DEFAULT_PROGRESS;
                    return (
                      <ActivityItem
                        key={activity.id}
                        activity={activity}
                        unit={unit}
                        subunit={{ ...subunit, id: subunitId }}
                        unitId={unitId}
                        progress={subunitProgress}
                      />
                    );
                  })}
              </div>
            </div>
          ))}
        {/* add unit level activities */}
        {openUnit === unit.index &&
          unit.activities.map((activity) => {
            const unitProgress =
              progressCookie[course.id].completion_data.units[unitId as UnitId] ?? DEFAULT_PROGRESS;
            return (
              <div
                key={activity.id}
                className='flex flex-col items-start hover:cursor-pointer px-[16px]'
              >
                <ActivityItem
                  activity={activity}
                  unit={unit}
                  unitId={unitId}
                  progress={unitProgress}
                />
              </div>
            );
          })}
      </div>
    );
  };

  return (
    <div className='progress-nav max-w-[280px] w-full flex flex-col flex-1 min-h-0'>
      <div className='py-4 px-2 flex justify-center items-center flex-row'>
        <Button onClick={onClick} text className='p-2 hover:text-red-600'>
          <CircleXIcon className='w-5 h-5' />
        </Button>
        <Button
          onClick={() => router.push(`/student/course`)}
          className='text-sm flex flex-row items-center gap-2 bg-surface_white text-text_gray_600 hover:text-text_contrast border border-border_gray_300 hover:bg-primary hover:border-primary h-fit'
        >
          <HomeIcon className='w-5 h-5' /> Back to Course overview
        </Button>
      </div>
      <div className='flex flex-col gap-2 p-4 border-y-border_sidenav border-y'>
        <div className='text-lg font-semibold text-center'>{course?.name}</div>
        <div className='flex flex-col gap-2'>
          <div className='flex items-center gap-[12px]'>
            <div className='flex-1 bg-gray-200 rounded-full h-2'>
              <div
                className='bg-primary h-2 rounded-full transition-all duration-300'
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
            <div className='text-sm font-medium'>{completionPercentage}%</div>
          </div>
          <div className='text-xs text-gray-500'>
            {completed} of {total} activities completed
          </div>
        </div>
      </div>
      {/* Units */}
      <div className='flex flex-col py-4 gap-4 flex-1 min-h-0 overflow-y-auto'>
        {Object.entries(course.units).map(([unitId, unit], index) => (
          <div key={index} className='px-1'>
            <DropdownMenu unit={unit} unitId={unitId} />
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProgressNav;
